import { ActivatedRoute, Data, Router, RouterLink } from '@angular/router';
import { AuthService, NotificationService, VesselService } from '@services';
import { CardImageComponent } from '@components/card-image/card-image.component';
import { Component, inject, signal } from '@angular/core';
import { HttpParams, httpResource } from '@angular/common/http';
import { MarkdownPipe } from '@pipes';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { PromptComponent } from '@components/prompt/prompt.component';
import { environment } from '@environment';

import {
  AttachmentListResponse,
  Permission,
  VesselEntry,
} from '@models';

@Component({
  selector: 'app-vessel-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  imports: [
    CardImageComponent,
    MarkdownPipe,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    RouterLink,
  ],
})
export class VesselItemComponent {
  route = inject(ActivatedRoute);
  router = inject(Router);

  // services
  authService = inject(AuthService);
  vesselService = inject(VesselService);
  notificationService = inject(NotificationService);

  // Data & Permissions
  vesselEntry = signal<VesselEntry>(this.route.snapshot.data['vessel']);
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  // Dialog box setup
  dialog = inject(MatDialog);

  // Images
  defaultImagePageSize = 10;

  attachAddr = `${environment.kinkoHost}/dataset/${environment.vesselDatasetId}/attachment/`
  imagePage = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  imagePageSize = signal(parseInt(this.route.snapshot.queryParams['size']) || this.defaultImagePageSize);
  images = httpResource<AttachmentListResponse>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.imagePage() * this.imagePageSize())
      .set('take', this.imagePageSize())
      .set('count', true)
      .set('recordId', this.vesselEntry().id)
      .set('order', 'created:asc')

    return `${this.attachAddr}?${httpParams.toString()}`;
  });

  constructor() {
    this.route.data.subscribe((data: Data) => {
      this.vesselEntry.set(data['vessel']);
      this.permissions.set(data['permissions']);
    });
  }

  hasHistory(): boolean {
    if (
      this.vesselEntry().content.history ||
      this.vesselEntry().content.constructionYear ||
      this.vesselEntry().content.constructionPlace ||
      this.vesselEntry().content.shipwreckedYear ||
      this.vesselEntry().content.shipwreckedLocation
    ) {
      return true;
    }
    return false;
  }

  deleteVessel() {
    const dialogRef = this.dialog.open(PromptComponent, {
      data: {
        title: 'Delete vessel from register',
        content: `Confirm that you want to delete this vessel.

          Note: Any images associated with the vessel have to be removed before
          you can delete this entry from the register.`,
        minwidth: '360px',
        width: '480px',
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.vesselService.removeVessel(this.vesselEntry().id).subscribe({
        next: () => {
          this.router.navigate(['/vessel']);
          this.notificationService.success('Vessel successfully removed from register.');
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to remove vessel from register.')
        }
      });
    })
  }
}
